// Email contact helpers (2026-10-08): the header's customer email, and the
// reply box's Reply-All CC. Pure, so the rules are unit-tested
// (test/emailCc.test.js) and the components only render them.
import { headerChannelFor } from '@cxhub/shared'

function norm(address) {
  return String(address ?? '').trim().toLowerCase()
}

// The header's Customer Email on an email conversation is the ORDER's email
// (orderSummary.customerEmail), never the sender: an email sender is often
// a form relay, an internal notice or an agent. No order, no email. Null on
// any other channel (eBay shows its own buyer fields).
export function headerCustomerEmail(conversation, orderSummary) {
  // A task contact with a Website order shows it too (headerChannelFor()).
  if (headerChannelFor(conversation) !== 'm365_email') return null
  return orderSummary?.customerEmail ?? null
}

// The Reply-All CC default: everyone else the inbound message went to,
// (inbound To ∪ CC) − CXHub's own addresses − the reply's To, lowercased
// and deduplicated, in the message's own order (To first, then CC).
export function replyAllCcDefault({ inbound, ownAddresses = [], toAddress = '' } = {}) {
  const own = new Set((ownAddresses ?? []).map(norm))
  const to = norm(toAddress)
  const out = []
  for (const raw of [...(inbound?.toAddresses ?? []), ...(inbound?.ccAddresses ?? [])]) {
    const address = norm(raw)
    if (!address || own.has(address) || address === to || out.includes(address)) continue
    out.push(address)
  }
  return out
}

const ADDRESS = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/

// The CC box's comma- (or semicolon-) separated text -> the addresses to
// send, lowercased and deduplicated, plus any entry that isn't one address.
export function parseCcText(text) {
  const addresses = []
  const invalid = []
  for (const part of String(text ?? '').split(/[,;]/)) {
    const entry = part.trim()
    if (!entry) continue
    if (!ADDRESS.test(entry)) { invalid.push(entry); continue }
    const address = entry.toLowerCase()
    if (!addresses.includes(address)) addresses.push(address)
  }
  return { addresses, invalid }
}

// Whether the CC field shows, and whether it can be used: a Reply to Email on
// an email conversation only (never a new outbound email, never eBay or an
// eBay-client email). In test mode it shows disabled, since the test clamp
// is one recipient.
export function ccFieldState({ channel, replyMode, isEbayClientEmail = false, outboundMode = null } = {}) {
  const visible = channel === 'm365_email' && !isEbayClientEmail && replyMode === 'reply'
  const testMode = outboundMode === 'test'
  return {
    visible,
    disabled: visible && testMode,
    note: visible && testMode ? "CC isn't available in test mode" : null,
  }
}

// What a send carries: the CCs when the field is usable, else nothing. A
// malformed entry is sent as typed, never dropped, so the server's CC door
// refuses the send and says why rather than mailing fewer people than shown.
export function ccAddressesForSend(fieldState, text) {
  if (!fieldState?.visible || fieldState.disabled) return []
  const { addresses, invalid } = parseCcText(text)
  return [...addresses, ...invalid]
}
